<template>
  <div class="secret-form" :class="{ 'has-kc': hasKeyColor }" :style="kcStyle">
    <div :class="customClass">
      <div class="secret-form__inner">
        <div v-if="!hideTitle" class="secret-form__tit">
          <slot name="title">
            <p>
              <template v-for="(line, lineIdx) in titleLines" :key="lineIdx">
                <template v-for="(part, partIdx) in line" :key="partIdx">
                  <span v-if="part.highlight" class="primary-color">{{ part.text }}</span>
                  <template v-else>{{ part.text }}</template>
                </template>
                <br v-if="lineIdx < titleLines.length - 1" />
              </template>
            </p>
          </slot>
        </div>

        <div class="secret-form__cont">
          <template v-if="!isSingleCategory">
            <div
              v-for="(row, rowIdx) in rowGroups"
              :key="rowIdx"
              class="radio__wrap"
              :class="{
                'extra-category': rowIdx > 0,
                'only-internet': rowIdx === 0 && isOnlyInternet,
                'cols-half': isHalfWidth,
                [`layout-${categoryLayout}`]: true,
              }"
              :style="{ '--asf-grid-cols': colsPerRow }"
            >
              <div v-for="cat in row" :key="cat.id" class="radio__item">
                <category-button
                  :item="cat"
                  :select-list="categorySelectList"
                  :display="categoryDisplay"
                  :icon-position="categoryIconPosition"
                  :class="{
                    internet: cat.id === MAIN_CATEGORY_MAP.INTERNET && activeIncludeInternet,
                  }"
                  @click="onClickCategory"
                />
              </div>
              <div
                v-if="categoryLayout !== 'scroll' && isUsimVisible && row.some(c => c.id === MAIN_CATEGORY_MAP.INTERNET)"
                class="checkbox-container"
                @click.stop
              >
                <check-box class="usim" :label="usimLabel" v-model="requestParams.checkUsim" />
              </div>
              <div
                v-if="categoryLayout !== 'scroll' && activeIncludePhone && row.some(c => c.id === EXTRA_CATEGORY.PHONE)"
                class="checkbox-container"
                @click.stop
              >
                <span class="phone-preorder-label">{{ phoneHint }}</span>
              </div>
            </div>
          </template>

          <!-- 가로 스크롤 모드: USIM/사전예약 안내는 스크롤 트랙 안이 아니라 아래에 고정 노출 -->
          <div
            v-if="!isSingleCategory && categoryLayout === 'scroll' && (isUsimVisible || activeIncludePhone)"
            class="radio__wrap scroll-extras"
          >
            <div v-if="isUsimVisible" class="checkbox-container" @click.stop>
              <check-box class="usim" :label="usimLabel" v-model="requestParams.checkUsim" />
            </div>
            <div v-if="activeIncludePhone" class="checkbox-container" @click.stop>
              <span class="phone-preorder-label">{{ phoneHint }}</span>
            </div>
          </div>

          <div
            v-if="isSingleCategory && (isUsimVisible || activeIncludePhone)"
            class="radio__wrap single-category"
          >
            <div v-if="isUsimVisible" class="checkbox-container" @click.stop>
              <check-box class="usim" :label="usimLabel" v-model="requestParams.checkUsim" />
            </div>
            <div v-if="activeIncludePhone" class="checkbox-container" @click.stop>
              <span class="phone-preorder-label">{{ phoneHint }}</span>
            </div>
          </div>

          <div class="input__wrap">
            <div v-if="!isKakaoLogin" class="custom-form-container">
              <div v-if="!isNameHidden">
                <c-input-text
                  :label="nameLabel"
                  :placeholder="namePlaceholder"
                  v-model="nameValue"
                  :validation="validateNameString"
                  check-empty-validation
                />
                <c-input-help-text
                  v-if="nameValue && !validateNameString(nameValue)"
                  :type="InputHelpTextType.Error"
                  :text="VALIDATE_ERROR_MESSAGE[VALIDATE_KEY.NAME]"
                />
              </div>

              <div>
                <c-input-text
                  :label="phoneNumberLabel"
                  :placeholder="phoneNumberPlaceholder"
                  v-model="phoneNumberValue"
                  :validation="validatePhone"
                  :mask="CInputTextDataMaskType.Phone"
                  check-empty-validation
                  @vue:mounted="setPhoneInputAttrs"
                />
                <c-input-help-text
                  v-if="phoneNumberValue && !validatePhone(phoneNumberValue.replaceAll('-', ''))"
                  :type="InputHelpTextType.Error"
                  :text="VALIDATE_ERROR_MESSAGE[VALIDATE_KEY.PHONE_NUMBER]"
                />
              </div>

            </div>
          </div>

          <slot name="additional-fields" />

          <agree-box
            :terms-list="isOnlyLifecareSelected ? [] : termsLists"
            :extra-terms-list="applyLifecare ? lifecareTermsList : []"
            :is-all-checked="isAllCheckedWithExtra"
            :show-marketing-hint="isShowMarketingHint"
            :consent-title="consentTitle"
            :marketing-hint-text="marketingHintText"
            @toggle-all="onToggleAllAgree"
            @open-detail="openTermsModal"
          />

          <c-button
            v-if="!isKakaoLogin"
            id="submit-btn"
            :label="submitButtonLabel"
            :size="CButtonSize.XL"
            :disabled="!isSubmitEnabled || isSubmitting"
            fill
            @click="onSubmit"
          />
          <c-button
            v-else
            id="submit-btn"
            class="kakao-request"
            label="카카오톡 바로 신청하기"
            :size="CButtonSize.XL"
            :disabled="!isRequiredAllCheck"
            fill
            @click="onSubmitKakaoRequest"
          />
        </div>
      </div>
    </div>
  </div>

  <terms-modal
    v-if="isTermsModal"
    :title="selectTerm?.title"
    :content="selectTerm?.content"
    :is-mobile="isMobile"
    @close="closeTermsModal"
  />

  <night-consent-modal
    v-if="isNightConsentOpen"
    :title="nightConsentTitle"
    :desc="nightConsentDesc"
    :cta-label="nightConsentCta"
    @confirm="onNightConsentConfirm"
    @close="isNightConsentOpen = false"
  />

  <success-modal
    v-if="isModal"
    :is-modal="isModal"
    :hash-string="hashString"
    :main-category="submittedMainCategory"
    :inflow-path="modalInflowPath"
    :inflow-path-detail="modalInflowPathDetail"
    :message="successMessage"
    :hide-additional-info-btn="hideAdditionalInfoBtn"
    :night-callback-time="showNightCallbackAlert ? nightCallbackTime : null"
    @confirm="onMoveHome"
    @move-to-self-form="onMoveToSelfForm"
  />

  <extra-category-modal v-if="isExtraCategoryModal" @confirm="onMoveHome" />

  <lifecare-modal
    v-if="isLifecareModal"
    :night-callback-time="showNightCallbackAlert ? nightCallbackTime : null"
    @confirm="onMoveHome"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import {
  CButton,
  CButtonSize,
  CInputHelpText,
  CInputText,
  CInputTextDataMaskType,
  InputHelpTextType,
} from '@aj-fe/aj-vue-core'

import AgreeBox from './components/AgreeBox.vue'
import CategoryButton from './components/CategoryButton.vue'
import CheckBox from './components/CheckBox.vue'
import ExtraCategoryModal from './components/ExtraCategoryModal.vue'
import LifecareModal from './components/LifecareModal.vue'
import NightConsentModal from './components/NightConsentModal.vue'
import SuccessModal from './components/SuccessModal.vue'
import TermsModal from './components/TermsModal.vue'

import {
  EARLY_BIRD_EVENT_TYPE,
  EXTRA_CATEGORY,
  INFLOW_PATH_VALUES,
  MAIN_CATEGORY_MAP,
  MINUTE,
  NAVER_SEARCH_INFLOW_PATH,
  RECEIVE_PATH,
  RECEIVE_PATH_MAP,
  type ReceivePathKey,
  SJ_TYPE_VALUES,
} from './constants'
import { buildTermsHistories, isMarketingTerm } from './counselTerms'
import { LIFECARE_TERMS, type LifecareTerm } from './lifecareTerms'
import {
  encryptPhoneNumber,
  fetchTerms,
  submitCounselRequest,
  type CallReceiveConsent,
  type CounselRequestBody,
  type CounselRequestLifeCareTermsRq,
  type CounselRequestResponse,
} from './api'
import { useNightConsent } from './useNightConsent'
import { useQuickRegister, type CounselForm, type TermItem } from './useQuickRegister'
import {
  VALIDATE_ERROR_MESSAGE,
  VALIDATE_KEY,
  getUtmParams as defaultGetUtmParams,
  isBsaNaver as defaultIsBsaNaver,
  validateNameString,
  validatePhone,
} from './utils'
import type { CategoryItem, LoginInfo } from './types'

const ICON_BASE = 'https://cdn.ajd.kr/images'
const CATEGORY_ICONS = {
  internet: `${ICON_BASE}/platform/reservation/ico-1.webp`,
  rental: `${ICON_BASE}/platform/reservation/ico-2.webp`,
  mvno: `${ICON_BASE}/platform/reservation/ico-4.webp`,
  phone: `${ICON_BASE}/platform/reservation/ico-3.webp`,
  moving: `${ICON_BASE}/vue-core/illust/box.webp`,
  lifecare: `${ICON_BASE}/vue-core/illust/letter.webp`,
}

const API_BASE_URL_BETA = 'https://platform-api.beta.ajdcorp.com'
const API_BASE_URL_PROD = 'https://api-platform.ajd.co.kr'
// 운영 도메인 목록 — 이 도메인에서 열리면 자동으로 prod 환경.
// 신규 운영 도메인 추가 시 여기에 등록 (ajd-landings tracker.js PROD_HOSTNAME 과 동기화 필요).
const PROD_HOSTNAMES = ['landing.ajd.co.kr']

const DEFAULT_TITLE_TEXT = '놓치고 있던 내 최대 지원금\n아정당이 알려드릴게요!'
const DEFAULT_TITLE_HIGHLIGHT = '내 최대 지원금'
const DEFAULT_SUBMIT_LABEL = '내 최대 지원금 안내 받기'

// ── Key Color → 5색 자동 생성 (HSL 기반) ──
// 키컬러를 HSL 로 변환해 명도만 단계별로 이동(Default/Hover/Press/Disabled/Light).
// 채도 85% 이하 색은 보정 0(그대로), 형광색(채도>85%)만 옅은 톤의 채도를 자동으로 눌러줌.
const clampNum = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

function hexToHsl(hex: string): [number, number, number] | null {
  let m = hex.trim().replace(/^#/, '')
  if (m.length === 3) m = m.split('').map(c => c + c).join('')
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return null
  const r = parseInt(m.slice(0, 2), 16) / 255
  const g = parseInt(m.slice(2, 4), 16) / 255
  const b = parseInt(m.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === r) h = (((g - b) / d) % 6 + 6) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  const l = (max + min) / 2
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  return [h, s, l]
}

// [name, refL, lighten, desat]
const KC_STEPS: ReadonlyArray<readonly [string, number, number, number]> = [
  ['default', 0.49, 0, 0],
  ['hover', 0.41, 0, 0],
  ['press', 0.329, 0, 0],
  ['disabled', 0.81, 0.5, 0.35],
  ['light', 0.959, 0.85, 0.55],
]

function keyColorPalette(key: string): Record<string, string> | null {
  const hsl = hexToHsl(key)
  if (!hsl) return null
  const [h, s, l] = hsl
  const delta = l - 0.49
  const excess = clampNum((s - 0.85) / 0.15, 0, 1) // 형광색일 때만 0보다 커짐
  const out: Record<string, string> = {}
  for (const [name, refL, lighten, desat] of KC_STEPS) {
    const sat = s * (1 - excess * desat)
    const lig = clampNum(refL + delta * (1 - lighten), 0.04, 0.985)
    out[`--kc-${name}`] = `hsl(${h.toFixed(1)} ${(sat * 100).toFixed(1)}% ${(lig * 100).toFixed(1)}%)`
  }
  return out
}

const props = withDefaults(
  defineProps<{
    inflowPath?: string
    inflowPathDetail?: string
    sjType?: string
    sjCode?: string
    sjName?: string
    prodName?: string
    modalInflowPath?: string | null
    modalInflowPathDetail?: string | null
    receivePath?: ReceivePathKey
    titleText?: string
    titleHighlight?: string
    hideTitle?: boolean
    categories?: string
    rentalLabel?: string
    hideUsim?: boolean
    customClass?: string
    isKakaoLogin?: boolean
    submitBtnLabel?: string
    holdMessage?: string
    isAdditionalFieldsValid?: boolean
    buttonName?: string
    isHideName?: boolean
    isSendAlimtalk?: boolean
    isShowMarketingHint?: boolean
    isEncryptPhone?: boolean
    additionalSummary?: string
    usimLabel?: string
    phoneHint?: string
    successMessage?: string
    hideAdditionalInfoBtn?: boolean
    // ── UI 커스터마이징 (표현 레이어만 — 데이터/검증/동의효력/이벤트는 불변) ──
    internetLabel?: string
    mvnoLabel?: string
    phoneLabel?: string
    movingLabel?: string
    lifecareLabel?: string
    internetIcon?: string
    rentalIcon?: string
    mvnoIcon?: string
    phoneIcon?: string
    movingIcon?: string
    lifecareIcon?: string
    categoryDisplay?: 'icon-text' | 'text' | 'icon'
    categoryIconPosition?: 'top' | 'bottom' | 'left' | 'right'
    categoryLayout?: 'grid' | 'list' | 'scroll'
    gridColumns?: number
    nameLabel?: string
    namePlaceholder?: string
    phoneNumberLabel?: string
    phoneNumberPlaceholder?: string
    consentTitle?: string
    marketingHintText?: string
    keyColor?: string
    // ── 야간동의 (RNT-290) — 모달 개방 슬롯(미지정 시 컴포넌트 default). 선택지/시간옵션 라벨은 고정(노출 X) ──
    enableNightConsent?: boolean
    nightConsentTitle?: string
    nightConsentDesc?: string
    nightConsentCta?: string
    getLoginInfo?: () => LoginInfo | null
    getUtmParams?: () => Record<string, string>
    isBsaNaver?: () => boolean
    onToast?: (message: string, type?: 'error' | 'info') => void
  }>(),
  {
    inflowPath: '',
    inflowPathDetail: '',
    sjType: '',
    sjCode: '',
    sjName: '',
    prodName: '',
    receivePath: 'PLATFORM',
    modalInflowPath: null,
    modalInflowPathDetail: null,
    titleText: DEFAULT_TITLE_TEXT,
    titleHighlight: DEFAULT_TITLE_HIGHLIGHT,
    hideTitle: false,
    categories: 'internet,rental,mvno,phone,moving,lifecare',
    rentalLabel: '',
    hideUsim: false,
    customClass: '',
    isKakaoLogin: false,
    submitBtnLabel: '',
    holdMessage: '',
    isAdditionalFieldsValid: true,
    buttonName: '',
    isHideName: false,
    isSendAlimtalk: true,
    isShowMarketingHint: false,
    isEncryptPhone: true,
    additionalSummary: '',
    usimLabel: '휴대폰 통신사 바꾸고 추가 현금사은품 받기',
    phoneHint: '휴대폰 구입 안내링크를 보내드려요.',
    successMessage: '',
    hideAdditionalInfoBtn: false,
    // UI 커스터마이징 기본값 — 빈/기본이면 현행과 100% 동일
    internetLabel: '',
    mvnoLabel: '',
    phoneLabel: '',
    movingLabel: '',
    lifecareLabel: '',
    internetIcon: '',
    rentalIcon: '',
    mvnoIcon: '',
    phoneIcon: '',
    movingIcon: '',
    lifecareIcon: '',
    categoryDisplay: 'icon-text',
    categoryIconPosition: 'top',
    categoryLayout: 'grid',
    gridColumns: 0,
    nameLabel: '이름',
    namePlaceholder: '이름을 입력해주세요',
    phoneNumberLabel: '휴대폰 번호',
    phoneNumberPlaceholder: '휴대폰번호를 입력해주세요',
    consentTitle: '전체 동의',
    marketingHintText: '',
    keyColor: '',
    // 야간동의 (RNT-290) 기본값
    enableNightConsent: true,
    nightConsentTitle: '',
    nightConsentDesc: '',
    nightConsentCta: '',
    getLoginInfo: undefined,
    getUtmParams: undefined,
    isBsaNaver: undefined,
    onToast: undefined,
  },
)

// keyColor 가 유효하면 5색 팔레트(--kc-*)를 루트에 주입 → 카드·체크박스·인풋·버튼이 자동 반영.
// 미지정/무효면 빈 객체 → 현행 디자인시스템 색 그대로(회귀 0).
const kcStyle = computed<Record<string, string>>(() => keyColorPalette(props.keyColor) ?? {})
const hasKeyColor = computed(() => Object.keys(kcStyle.value).length > 0)

const emit = defineEmits<{
  (e: 'category-change', categories: CategoryItem[]): void
  (
    e: 'submit-success',
    payload: CounselRequestResponse & {
      name: string
      phoneNumber: string
      phoneHash?: string
      mktReceiveAgreement: boolean
      buttonName?: string
      preorderNotify?: string
      service_internet: boolean
      service_rental: boolean
      service_mvno: boolean
      service_phone: boolean
      service_moving: boolean
      service_lifecare: boolean
    },
  ): void
  (e: 'submit-error', payload: { error: unknown }): void
  (e: 'complete'): void
  (e: 'kakao-login', payload: Record<string, unknown>): void
  (
    e: 'move-to-self-form',
    payload: {
      mainCategory: string | null
      hashString: string | null
      inflowPath: string | null
      inflowPathDetail: string | null
    },
  ): void
}>()

const {
  termsLists,
  isAllCheckAgree,
  isAllWrite,
  isRequiredAllCheck,
  selectTerm,
  isTermsModal,
  counselForm,
  isModal,
  confirmTimer,
  openTermsModal,
  closeTermsModal,
  toggleAllAgree,
} = useQuickRegister()

// 환경 판단: 런타임 도메인(location.hostname)이 운영 도메인 목록에 있으면 prod, 아니면 beta.
// 강제 override 속성은 없음 — 운영 노출은 운영 도메인에서 열리는 것으로만 결정된다.
const isProdEnv = computed(() => {
  if (typeof window === 'undefined') return false
  return PROD_HOSTNAMES.includes(window.location.hostname)
})
const apiBaseUrl = computed(() => (isProdEnv.value ? API_BASE_URL_PROD : API_BASE_URL_BETA))

const isMobile = ref(false)
let mediaQuery: MediaQueryList | null = null
const onMediaChange = (e: MediaQueryListEvent) => {
  isMobile.value = e.matches
}

onMounted(() => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    mediaQuery = window.matchMedia('(max-width: 768px)')
    isMobile.value = mediaQuery.matches
    mediaQuery.addEventListener('change', onMediaChange)
  }
})

onBeforeUnmount(() => {
  if (mediaQuery) mediaQuery.removeEventListener('change', onMediaChange)
})

const urlParams =
  typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
const nameValue = ref(urlParams?.get('name') ?? '')
const phoneNumberValue = ref(urlParams?.get('phoneNumber') ?? '')

// 이름 input 은 기본 노출(is-hide-name 기본값 false). 숨기려는 페이지만 is-hide-name 으로 opt-in 숨김.
// custom element 속성은 문자열로 들어올 수 있어 빈 문자열("<ajd-secret-form is-hide-name>")·"true"/"1"/"yes" 를
// 명시적으로 숨김으로 정규화한다 (Vue 의 boolean 속성 강제변환 버전차와 무관하게 동작).
const isNameHidden = computed(() => {
  const v = props.isHideName as unknown
  return v === true || v === '' || v === 'true' || v === 1 || v === '1' || v === 'yes'
})

const hashString = ref<string | null>(null)
const submittedMainCategory = ref<string | null>(null)
const isExtraCategoryModal = ref(false)
const isLifecareModal = ref(false)
const isSubmitting = ref(false)

const internetCategory = (): CategoryItem => ({
  id: MAIN_CATEGORY_MAP.INTERNET,
  label: props.internetLabel || '인터넷',
  icon: props.internetIcon || CATEGORY_ICONS.internet,
  type: 'main',
})
const rentalCategory = (): CategoryItem => ({
  id: MAIN_CATEGORY_MAP.RENTAL,
  label: props.rentalLabel || '가전렌탈',
  icon: props.rentalIcon || CATEGORY_ICONS.rental,
  type: 'main',
})
const mvnoCategory = (): CategoryItem => ({
  id: EXTRA_CATEGORY.MVNO,
  label: props.mvnoLabel || '알뜰폰',
  icon: props.mvnoIcon || CATEGORY_ICONS.mvno,
  type: 'extra',
})
const phoneCategory = (): CategoryItem => ({
  id: EXTRA_CATEGORY.PHONE,
  label: props.phoneLabel || '휴대폰',
  icon: props.phoneIcon || CATEGORY_ICONS.phone,
  type: 'extra',
})
const movingCategory = (): CategoryItem => ({
  id: EXTRA_CATEGORY.MOVING,
  label: props.movingLabel || '이사',
  icon: props.movingIcon || CATEGORY_ICONS.moving,
  type: 'extra',
})
const lifecareCategory = (): CategoryItem => ({
  id: EXTRA_CATEGORY.LIFECARE,
  label: props.lifecareLabel || '100원 상조',
  icon: props.lifecareIcon || CATEGORY_ICONS.lifecare,
  type: 'lifecare',
})

const CATEGORY_FACTORIES: Record<string, () => CategoryItem> = {
  internet: internetCategory,
  rental: rentalCategory,
  mvno: mvnoCategory,
  phone: phoneCategory,
  moving: movingCategory,
  lifecare: lifecareCategory,
}

const visibleCategoryKeys = computed<string[]>(() =>
  props.categories
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(k => k in CATEGORY_FACTORIES),
)

const isSingleCategory = computed(() => visibleCategoryKeys.value.length === 1)
const isOnlyInternet = computed(
  () => visibleCategoryKeys.value.length === 1 && visibleCategoryKeys.value[0] === 'internet',
)

const allCategories = computed<CategoryItem[]>(() =>
  visibleCategoryKeys.value.map(k => CATEGORY_FACTORIES[k]()),
)

const HALF_WIDTH_TOTALS = new Set([2, 4])
const colsPerRow = computed(() => {
  if (props.categoryLayout === 'list') return 1
  if (props.gridColumns && props.gridColumns > 0) return props.gridColumns
  return HALF_WIDTH_TOTALS.has(allCategories.value.length) ? 2 : 3
})
const isHalfWidth = computed(() => colsPerRow.value === 2)
const rowGroups = computed<CategoryItem[][]>(() => {
  const items = allCategories.value
  // 가로 스크롤은 단일 행으로 묶어 nowrap 스크롤
  if (props.categoryLayout === 'scroll') return [items]
  const cols = colsPerRow.value
  const rows: CategoryItem[][] = []
  for (let i = 0; i < items.length; i += cols) {
    rows.push(items.slice(i, i + cols))
  }
  return rows
})

const initialState = (() => {
  const firstKey = visibleCategoryKeys.value[0] ?? 'internet'
  const factory = CATEGORY_FACTORIES[firstKey] ?? internetCategory
  const cat = factory()
  if (isSingleCategory.value) {
    if (cat.type === 'main') return { main: [cat], extra: [] as string[], lifecare: false }
    if (cat.type === 'extra') return { main: [], extra: [cat.id], lifecare: false }
    return { main: [], extra: [], lifecare: true }
  }
  if (cat.type === 'main') return { main: [cat], extra: [] as string[], lifecare: false }
  return { main: [internetCategory()], extra: [] as string[], lifecare: false }
})()

const requestParams = reactive({
  additionalCategoryList: initialState.main as CategoryItem[],
  checkUsim: false,
  extraCategories: initialState.extra,
  isPhonePreOrder: false,
})

const applyLifecare = ref(initialState.lifecare)
const lifecareTermsList = ref<LifecareTerm[]>(LIFECARE_TERMS.map(c => ({ ...c, isCheck: false })))

watch(applyLifecare, val => {
  if (!val) {
    lifecareTermsList.value.forEach(term => {
      term.isCheck = false
    })
  }
})

const buildLifeCareTermsAgreement = (): CounselRequestLifeCareTermsRq => {
  const agreement: CounselRequestLifeCareTermsRq = {
    agree01: 'N',
    agree02: 'N',
    agree03: 'N',
    agree04: 'N',
  }
  lifecareTermsList.value.forEach(term => {
    agreement[term.agreeKey] = term.isCheck ? 'Y' : 'N'
  })
  return agreement
}

const isAllCheckedWithExtra = computed(() => {
  if (!applyLifecare.value) return isAllCheckAgree.value
  return isAllCheckAgree.value && lifecareTermsList.value.every(t => t.isCheck)
})

const onToggleAllAgree = (val: boolean) => {
  toggleAllAgree(val)
  if (applyLifecare.value) {
    lifecareTermsList.value.forEach(term => {
      term.isCheck = val
    })
  }
}

const isOnlyLifecareSelected = computed(
  () =>
    requestParams.additionalCategoryList.length === 0 &&
    requestParams.extraCategories.length === 0 &&
    applyLifecare.value,
)

const isFormValid = computed(() => {
  if (!counselForm.value?.values) return false
  const values = counselForm.value.values
  const nameValid = isNameHidden.value || validateNameString(values[VALIDATE_KEY.NAME] ?? '')
  const phoneRaw = values[VALIDATE_KEY.PHONE_NUMBER]?.replaceAll('-', '')
  const phoneValid = !!phoneRaw && validatePhone(phoneRaw)
  return nameValid && phoneValid
})

const isLifecareTermsValid = computed(() => {
  if (!applyLifecare.value) return true
  return lifecareTermsList.value.filter(t => t.isRequired).every(t => t.isCheck)
})

const isSubmitEnabled = computed(() => {
  if (!props.isAdditionalFieldsValid) return false
  if (!isLifecareTermsValid.value) return false
  if (isOnlyLifecareSelected.value) return isFormValid.value
  return isAllWrite.value
})

const SUB_CATEGORY_SORT_LIST = [
  MAIN_CATEGORY_MAP.INTERNET,
  MAIN_CATEGORY_MAP.RENTAL,
  MAIN_CATEGORY_MAP.USIM,
] as const

const buildServiceCategories = () => {
  const mainIds = requestParams.additionalCategoryList.map(c => c.id)
  const extraIds = requestParams.extraCategories
  return {
    service_internet: mainIds.includes(MAIN_CATEGORY_MAP.INTERNET),
    service_rental: mainIds.includes(MAIN_CATEGORY_MAP.RENTAL),
    service_mvno: extraIds.includes(EXTRA_CATEGORY.MVNO),
    service_phone: extraIds.includes(EXTRA_CATEGORY.PHONE),
    service_moving: extraIds.includes(EXTRA_CATEGORY.MOVING),
    service_lifecare: applyLifecare.value,
  }
}

const isCategoryActive = (categoryId: string) =>
  requestParams.additionalCategoryList.some(item => item.id === categoryId)

const activeIncludeInternet = computed(() => isCategoryActive(MAIN_CATEGORY_MAP.INTERNET))
const activeIncludePhone = computed(() => requestParams.extraCategories.includes(EXTRA_CATEGORY.PHONE))

const categorySelectList = computed<{ id: string }[]>(() => {
  const list: { id: string }[] = [...requestParams.additionalCategoryList]
  requestParams.extraCategories.forEach(id => list.push({ id }))
  if (applyLifecare.value) list.push({ id: EXTRA_CATEGORY.LIFECARE })
  return list
})

const totalSelectedCount = computed(
  () =>
    requestParams.additionalCategoryList.length +
    requestParams.extraCategories.length +
    (applyLifecare.value ? 1 : 0),
)

watch(
  () => requestParams.additionalCategoryList,
  () => {
    if (!activeIncludeInternet.value) {
      requestParams.checkUsim = false
    }
  },
  { deep: true },
)

watch(
  () => requestParams.extraCategories,
  newVal => {
    if (!newVal.includes(EXTRA_CATEGORY.PHONE)) {
      requestParams.isPhonePreOrder = false
    }
  },
  { deep: true },
)

const formValues: Record<string, string> = reactive({
  ...(!isNameHidden.value && { [VALIDATE_KEY.NAME]: nameValue.value }),
  [VALIDATE_KEY.PHONE_NUMBER]: phoneNumberValue.value,
})

watch(nameValue, v => {
  if (!isNameHidden.value) formValues[VALIDATE_KEY.NAME] = v
})
watch(phoneNumberValue, v => {
  formValues[VALIDATE_KEY.PHONE_NUMBER] = v
})

counselForm.value = {
  values: formValues,
  validate: async () => {
    const errors: Record<string, boolean> = {}
    if (!isNameHidden.value && !validateNameString(nameValue.value)) {
      errors[VALIDATE_KEY.NAME] = true
    }
    const rawPhone = phoneNumberValue.value?.replaceAll('-', '')
    if (!rawPhone || !validatePhone(rawPhone)) {
      errors[VALIDATE_KEY.PHONE_NUMBER] = true
    }
    return { errors }
  },
  getValues: () => ({
    name: nameValue.value,
    phoneNumber: phoneNumberValue.value?.replaceAll('-', '') ?? '',
  }),
} satisfies CounselForm

const onClickCategory = (target: CategoryItem) => {
  if (target.type === 'lifecare') {
    if (applyLifecare.value && totalSelectedCount.value <= 1) return
    applyLifecare.value = !applyLifecare.value
    return
  }

  if (target.type === 'extra') {
    if (requestParams.extraCategories.includes(target.id)) {
      if (totalSelectedCount.value > 1) {
        requestParams.extraCategories = requestParams.extraCategories.filter(id => id !== target.id)
      }
    } else {
      requestParams.extraCategories.push(target.id)
    }
    return
  }

  const idx = requestParams.additionalCategoryList.findIndex(item => item.id === target.id)
  if (idx === -1) {
    requestParams.additionalCategoryList.push(target)
  } else if (totalSelectedCount.value > 1) {
    requestParams.additionalCategoryList.splice(idx, 1)
  }

  emit('category-change', requestParams.additionalCategoryList)
}

const splitMainAndAdditional = (target: string[]) => {
  const mainCategory = SUB_CATEGORY_SORT_LIST.find(id => target.includes(id)) ?? null
  const rest = mainCategory ? target.filter(id => id !== mainCategory) : []
  return {
    mainCategory,
    additionalCategoryList: rest.length > 0 ? rest : null,
  }
}

const buildCategorySplit = () => {
  const ids = requestParams.additionalCategoryList.map(c => c.id)
  const idsWithUsim = [...ids, ...(requestParams.checkUsim ? [MAIN_CATEGORY_MAP.USIM] : [])]
  return splitMainAndAdditional(idsWithUsim)
}

// ── 야간 수신 동의 (enableNightConsent) ──────────────────────────────────────
// 야간(20:30~08:00 KST)이면 카테고리와 무관하게(휴대폰/알뜰폰/이사만 포함 전부)
// 제출 전 동의 모달로 분기. 거부 시 콜백 문구는 전화 안내가 있는 메인/상조 완료모달에만
// 표시(extra 완료모달은 알림톡 안내라 콜백 개념 없음 → 문구 없이 기존 유지).
const { resolveNightState } = useNightConsent()
const isNightConsentOpen = ref(false)
const nightConsentBody = ref<CallReceiveConsent | null>(null)
const nightCallbackTime = ref('익일 10시 이후')
const showNightCallbackAlert = ref(false)
const isNightSubmit = ref(false)

const baseSubmitLabel = computed(() => props.submitBtnLabel || DEFAULT_SUBMIT_LABEL)
// 야간이면 제출 버튼이 동의 모달을 먼저 여므로 라벨을 '다음'으로 (모달 CTA = baseSubmitLabel).
const submitButtonLabel = computed(() =>
  props.enableNightConsent && isNightSubmit.value ? '다음' : baseSubmitLabel.value,
)

const onNightConsentConfirm = (payload: CallReceiveConsent) => {
  nightConsentBody.value = payload
  isNightConsentOpen.value = false
  onSubmit() // 동의 수집 후 재제출 → 이번엔 nightConsentBody 존재 → 실제 제출 진행
}

const isUsimVisible = computed(() => {
  if (!activeIncludeInternet.value) return false
  if (isSingleCategory.value) return true
  return !props.hideUsim
})

type TitlePart = { text: string; highlight: boolean }
const titleLines = computed<TitlePart[][]>(() => {
  const text = props.titleText || DEFAULT_TITLE_TEXT
  const highlight = props.titleHighlight
  return text.split('\n').map(line => {
    if (!highlight) return [{ text: line, highlight: false }]
    const idx = line.indexOf(highlight)
    if (idx < 0) return [{ text: line, highlight: false }]
    const parts: TitlePart[] = []
    if (idx > 0) parts.push({ text: line.slice(0, idx), highlight: false })
    parts.push({ text: highlight, highlight: true })
    const after = line.slice(idx + highlight.length)
    if (after) parts.push({ text: after, highlight: false })
    return parts
  })
})

const onSubmitSuccess = (responseHash: string | null = null) => {
  if (responseHash) hashString.value = responseHash
  isModal.value = true
  confirmTimer.value = setTimeout(() => {
    props.onToast?.('안내가 종료되었습니다', 'info')
    onMoveHome()
  }, 2 * MINUTE)
}

type BuildSubmitBodyArgs = {
  name: string
  phoneNumber: string
  mainCategory: string | null
  additionalCategoryList: string[] | null
  termsHistories: TermItem[]
  extraCategories: string[]
}

/**
 * 상조 필드 빌더. 2026-09-01 서버 확장으로 두 모드가 공존한다.
 *
 * ① 레거시 (`sj-code` 없음) — `sj-type` 이 enum(`WON_100`|`COMBINED`)일 때만 `sjType` 을 싣는다.
 *    미설정(기본 '')·허용값 외는 아예 미전송 → 서버가 기존과 동일하게 결합 상조로 처리한다.
 *    허용값 외 문자열은 서버 400 이라 여기서 거른다(기존 방어 유지).
 *
 * ② 신규 상품타입 (`sj-code` 있음) — `sjType` 이 자유 라벨('100원 상조')로 바뀌고
 *    `sjCode`/`sjName`/`prodName` 을 함께 보낸다. **이 모드에선 enum 화이트리스트를 적용하지 않는다.**
 *    적용하면 한글 라벨이 조용히 버려져 상조 구분 없이 접수된다(8/28 sj-type 유실과 같은 증상).
 *
 * `sj-code` 를 모드 분기자로 쓰는 이유: 신규 스키마에는 항상 sjCode 가 있고, 이러면
 * 기존 페이지들의 요청 본문이 한 바이트도 안 바뀐다.
 */
const buildSjFields = (): Record<string, string> => {
  const put = (k: string, v: string) => (v ? { [k]: v } : {})

  if (!props.sjCode) {
    return SJ_TYPE_VALUES.has(props.sjType) ? { sjType: props.sjType } : {}
  }

  return {
    ...put('sjType', props.sjType),
    sjCode: props.sjCode,
    ...put('sjName', props.sjName),
    ...put('prodName', props.prodName),
  }
}

const buildSubmitBody = ({
  name,
  phoneNumber,
  mainCategory,
  additionalCategoryList,
  termsHistories,
  extraCategories,
}: BuildSubmitBodyArgs): CounselRequestBody => {
  const utm = props.getUtmParams?.() ?? defaultGetUtmParams()
  const isBsa = props.isBsaNaver?.() ?? defaultIsBsaNaver()
  const holdMessage = props.holdMessage
  const loginInfo = props.getLoginInfo?.() ?? null

  return {
    phoneNumber,
    // is-hide-name 으로 이름 숨긴 페이지는 수집 자체를 안 하므로 null 전송. 기본(노출)은 입력값 전송.
    customerName: isNameHidden.value ? null : name,
    checkTerms: true,
    requestUrl: typeof window !== 'undefined' ? window.location.href : '',
    tokenExpireHours: 24,
    sendAlimTalk: !!props.isSendAlimtalk,
    applyLifecare: applyLifecare.value,
    additionalCategoryList: additionalCategoryList ? [...additionalCategoryList] : null,
    mainCategory,
    termsHistories: buildTermsHistories(termsHistories),
    receivePath: RECEIVE_PATH_MAP[props.receivePath] ?? RECEIVE_PATH,
    ...(props.inflowPath && INFLOW_PATH_VALUES.has(props.inflowPath) && { inflowPath: props.inflowPath }),
    ...(isBsa && { inflowPath: NAVER_SEARCH_INFLOW_PATH }),
    ...(props.inflowPathDetail && { inflowPathDetail: props.inflowPathDetail }),
    ...(holdMessage && { holdMessage }),
    ...(props.additionalSummary && { additionalSummary: props.additionalSummary }),
    ...(extraCategories.length > 0 && { extraCategories: [...extraCategories] }),
    ...(requestParams.isPhonePreOrder && {
      earlyBirdEventType: EARLY_BIRD_EVENT_TYPE.GALAXY_S_26,
    }),
    ...(applyLifecare.value && {
      lifeCareTermsAgreement: buildLifeCareTermsAgreement(),
    }),
    // 상조 필드 — **상조가 선택된 제출에만** 싣는다. 두 모드는 buildSjFields() 참고.
    ...(applyLifecare.value && buildSjFields()),
    userSn: loginInfo?.sn ?? null,
    ...utm,
  }
}

const routePostSubmitModal = (
  mainCategory: string | null,
  extraCategories: string[],
  responseHash: string,
) => {
  // 완료 모달 분기: 메인(인터넷/렌탈) 포함 > 상조 포함(메인 없음) > extra(휴대폰/알뜰폰/이사) > 상조만
  // 상조 포함이면 휴대폰/알뜰폰/이사를 같이 골라도 상조 완료모달(1833-7377) 우선 (extra보다 앞).
  if (mainCategory) {
    onSubmitSuccess(responseHash)
  } else if (props.enableNightConsent && applyLifecare.value) {
    isLifecareModal.value = true
  } else if (extraCategories.length > 0) {
    isExtraCategoryModal.value = true
  } else {
    isLifecareModal.value = true
  }
}

// 실제 제출 절차. 중복 제출 가드는 래퍼(onSubmit)가 전담한다 — 여기서 세우면
// 아래 await 들보다 늦어져 두 번째 클릭이 그 사이로 끼어든다.
//
// 반환값 = "접수가 실제로 나갔는가". 래퍼가 이 값으로 잠금 유지 여부를 정한다.
// 검증 탈락·동의 미체크·야간 동의 보류처럼 사용자가 다시 눌러야 하는 경로는 전부 false 다.
const runSubmit = async (): Promise<boolean> => {
  if (!counselForm.value) return false

  const { errors } = await counselForm.value.validate()
  if (Object.keys(errors).length > 0) return false

  if (!isOnlyLifecareSelected.value && !isAllWrite.value) {
    props.onToast?.('개인정보 수집 및 이용에 동의해주세요', 'error')
    return false
  }
  if (!isLifecareTermsValid.value) return false

  // 야간 수신 동의 게이트: 야간(20:30~08:00)이면 카테고리 무관 모두, 아직 미동의면
  // 동의 모달로 분기(제출 보류). 모달 확인 → onNightConsentConfirm → onSubmit 재호출.
  if (props.enableNightConsent && !nightConsentBody.value) {
    const { isNight, callbackTime } = await resolveNightState(apiBaseUrl.value)
    if (isNight) {
      nightCallbackTime.value = callbackTime
      isNightConsentOpen.value = true
      // 보류지 완료가 아니다 — false 를 돌려 잠금을 풀어야 모달 확인 후의
      // onNightConsentConfirm → onSubmit 재호출이 가드에 막히지 않는다.
      return false
    }
  }

  try {
    const { phoneNumber, name } = counselForm.value.getValues()
    // 미동의 이력도 저장(INT-209) — 동의 항목만 filter 하지 않고 전체를 isAgree 로 구분해 전송
    // 상조 단독은 일반 약관 UI 자체가 미노출(:terms-list) → 화면에 없던 약관이 거부 이력으로 남지 않도록 제외
    const termsHistories = isOnlyLifecareSelected.value ? [] : termsLists.value
    const mktReceiveAgreement = termsHistories.some(t => t.isCheck && isMarketingTerm(t))

    const { mainCategory, additionalCategoryList } = buildCategorySplit()
    submittedMainCategory.value = mainCategory
    const extraCategories = requestParams.extraCategories

    const params = buildSubmitBody({
      name,
      phoneNumber,
      mainCategory,
      additionalCategoryList,
      termsHistories,
      extraCategories,
    })

    // 야간 수신 동의 값(있을 때만) — 카테고리 무관 전 폼. 주간·미적용 시엔 생략.
    if (props.enableNightConsent && nightConsentBody.value) {
      params.callReceiveConsent = nightConsentBody.value
    }

    const [response, phoneHash] = await Promise.all([
      submitCounselRequest(apiBaseUrl.value, params),
      props.isEncryptPhone
        ? encryptPhoneNumber(apiBaseUrl.value, params.phoneNumber)
        : Promise.resolve(''),
    ])

    emit('submit-success', {
      ...response,
      name,
      phoneNumber: params.phoneNumber,
      ...(props.isEncryptPhone && { phoneHash }),
      mktReceiveAgreement,
      ...(props.buttonName && { buttonName: props.buttonName }),
      ...(requestParams.isPhonePreOrder && {
        preorderNotify: EARLY_BIRD_EVENT_TYPE.GALAXY_S_26,
      }),
      ...buildServiceCategories(),
    })

    // 야간 '낮에 받을게요'(거부) 시 성공 모달에 콜백 안내 문구 노출
    showNightCallbackAlert.value = nightConsentBody.value?.isNightCallReceiveConsent === false
    routePostSubmitModal(mainCategory, extraCategories, response.hashString)
    return true
  } catch (err) {
    console.warn(err)
    emit('submit-error', { error: err })
    return false
  }
}

/**
 * 중복 제출 차단. 두 가지를 동시에 만족해야 한다.
 *
 * 1. 플래그를 **첫 await 앞**에서 세운다. runSubmit 안에서 세우면 validate()/야간판정을
 *    await 하는 사이에 두 번째 클릭이 가드를 그대로 통과한다.
 * 2. **접수에 성공하면 잠금을 유지한다.** 예전엔 finally 로 무조건 풀어서, 완료 직후
 *    버튼이 되살아나 두 번째 탭이 그대로 통과했다(응답이 빠를수록 창이 잘 열린다 —
 *    운영 실측: 같은 초에 2건, 29초 뒤 1건 추가 접수). 성공하면 완료 모달로 넘어가므로
 *    버튼이 다시 살아날 이유가 없다.
 *
 * 반대로 검증 탈락·동의 미체크·야간 동의 보류·API 에러는 사용자가 다시 눌러야 하므로
 * 반드시 잠금을 푼다. 특히 야간 동의는 모달 확인 후 onSubmit 을 재호출하는 구조라,
 * 여기서 잠긴 채로 두면 야간 시간대 제출이 아예 불가능해진다.
 */
const onSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true
  let submitted = false
  try {
    submitted = await runSubmit()
  } finally {
    if (!submitted) isSubmitting.value = false
  }
}

const onSubmitKakaoRequest = () => {
  if (!isRequiredAllCheck.value) return

  const { mainCategory, additionalCategoryList } = buildCategorySplit()
  submittedMainCategory.value = mainCategory
  // 미동의 이력도 저장(INT-209) — 동의 항목만 filter 하지 않고 전체를 isAgree 로 구분해 전송
  // 상조 단독은 일반 약관 UI 자체가 미노출(:terms-list) → 화면에 없던 약관이 거부 이력으로 남지 않도록 제외
  const termsHistories = isOnlyLifecareSelected.value ? [] : termsLists.value

  const utm = props.getUtmParams?.() ?? defaultGetUtmParams()
  const isBsa = props.isBsaNaver?.() ?? defaultIsBsaNaver()

  emit('kakao-login', {
    additionalCategoryList: additionalCategoryList ? [...additionalCategoryList] : null,
    mainCategory,
    termsHistories: buildTermsHistories(termsHistories),
    receivePath: RECEIVE_PATH_MAP[props.receivePath] ?? RECEIVE_PATH,
    ...(props.inflowPath && INFLOW_PATH_VALUES.has(props.inflowPath) && { inflowPath: props.inflowPath }),
    ...(isBsa && { inflowPath: NAVER_SEARCH_INFLOW_PATH }),
    ...(props.inflowPathDetail && { inflowPathDetail: props.inflowPathDetail }),
    ...utm,
    mkt_receive_agreement: termsHistories.some(t => t.isCheck && isMarketingTerm(t)),
    serviceCategories: buildServiceCategories(),
  })
}

const onMoveHome = () => {
  if (confirmTimer.value) {
    clearTimeout(confirmTimer.value)
    confirmTimer.value = null
  }
  isModal.value = false
  isExtraCategoryModal.value = false
  isLifecareModal.value = false
  // ⚠️ 여기서 isSubmitting 을 풀지 않는다. 접수는 되돌릴 수 없는 상태고, 이 함수는
  // **성공한 제출 뒤에만** 불린다(완료 모달 3종의 @confirm + onSubmitSuccess 의 2분 타이머).
  // 풀면 완료 모달을 닫은 직후 폼이 입력값까지 그대로 보이면서 버튼이 되살아나 두 번째
  // 접수가 나간다 — 운영 실측의 29초·3분 간격 중복이 이 경로다.
  // 완료 후 화면 전환은 호스트 책임이다: emit('complete') 를 받아 페이지를 옮기거나
  // 컴포넌트를 새로 마운트한다. 진짜 2회차는 새로고침/재마운트가 경계다.
  // 야간 동의 상태 초기화 → 다음 제출은 다시 판정/재수집
  nightConsentBody.value = null
  showNightCallbackAlert.value = false
  emit('complete')
}

const onMoveToSelfForm = (payload: {
  mainCategory: string | null
  hashString: string | null
  inflowPath: string | null
  inflowPathDetail: string | null
}) => {
  emit('move-to-self-form', payload)
}

// 휴대폰 input 번호 자동완성(autocomplete="tel") — CInputText 에 prop 이 없어 마운트 시 내부 input 에 직접 부여.
// (숫자 키패드 inputmode="numeric" 은 aj-vue-core 1.1.64+ 가 Phone 마스크에 자동 적용 — PLAT-5705)
function setPhoneInputAttrs(vnode: { el?: unknown }) {
  const input = (vnode.el as HTMLElement | null)?.querySelector?.('input')
  input?.setAttribute('autocomplete', 'tel')
}

onMounted(async () => {
  if (requestParams.additionalCategoryList.length > 0) {
    emit('category-change', requestParams.additionalCategoryList)
  }
  // 야간 여부를 미리 판정해 제출 버튼 라벨('다음') 반영 (서버시각 prime 겸용)
  if (props.enableNightConsent) {
    resolveNightState(apiBaseUrl.value).then(({ isNight }) => {
      isNightSubmit.value = isNight
    })
  }
  try {
    const fetched = await fetchTerms(apiBaseUrl.value)
    termsLists.value = fetched.map(t => ({
      ...t,
      title: t.title ?? '',
      isRequired: t.isRequired ?? false,
      isCheck: false,
    }))
  } catch (err) {
    console.warn('[ajd-secret-form] terms fetch failed:', err)
  }
})
</script>


<style lang="scss">
:host {
  display: block;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, sans-serif;

  /* ── UI 커스터마이징 CSS 변수 (호스트 오버라이드용) — 미지정 시 fallback = 현행값 ── */
  /* 카테고리 카드 (선택 전/후) */
  --asf-card-bg: transparent;
  --asf-card-bg-selected: #e8effd;
  --asf-card-border: #dee2e6;
  --asf-card-border-selected: #145ce6;
  --asf-card-border-width: 1px;
  --asf-card-border-width-selected: 2px;
  --asf-card-radius: 4px;
  /* --asf-card-text 는 기본값 미선언 → 미지정 시 호스트 색 상속(현행 보존). 지정 시 override */
  /* 입력 필드 (.c-input-text 외곽) */
  --asf-input-bg: #fff;
  --asf-input-border: #cfd4d9;
  --asf-input-border-width: 1px;
  --asf-input-radius: 10px;
  /* 동의 박스 */
  --asf-consent-bg: transparent;
  --asf-consent-border: var(--c-gray300, #cfd4d9);
  --asf-consent-radius: 12px;
  --asf-consent-chevron: #acb3ba;
  /* 체크박스 */
  --asf-check-accent: #145ce6;
  --asf-check-border: #c1c8cf;
  /* CTA — --asf-cta-bg 기본값은 선언하지 않음(선언하면 var() fallback 이 항상 그 값으로 막혀 key 를 못 따라감).
     CTA 규칙이 var(--asf-cta-bg, var(--kc-default, #145ce6)) 로 처리: 미지정 시 key Default(없으면 블루), 호스트 지정 시 override */
  --asf-cta-fg: #fff;
  --asf-cta-radius: 12px;
}

.secret-form {
  .white-box {
    width: 500px;
    border-radius: 16px;
    box-shadow: 0 4px 20px #2223;
    background: #fff;
    padding: 32px 40px;
  }

  &__tit {
    margin-bottom: 36px;

    p {
      color: #1f2429;
      font-family: Pretendard, sans-serif;
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 2.125rem;
      text-align: left;
    }
  }

  &__cont {
    .radio__wrap {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      justify-content: center;
      align-items: flex-start;

      &.extra-category {
        margin-top: 10px;
      }

      .radio__item {
        flex: 0 0
          calc((100% - (var(--asf-grid-cols, 3) - 1) * 10px) / var(--asf-grid-cols, 3));
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      &.cols-half .radio__item {
        flex: 0 0 calc((100% - 10px) / 2);
      }

      /* 가로 스크롤 레이아웃 (categoryLayout='scroll') */
      &.layout-scroll {
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        /* 베이스의 justify-content:center 가 오버플로 시 첫 아이템을 왼쪽으로 잘라먹음 → 시작 정렬 */
        justify-content: flex-start;

        .radio__item {
          flex: 0 0 calc((100% - 20px) / 3);
        }
      }

      /* 스크롤 트랙 밖(아래)에 노출되는 USIM/사전예약 안내 */
      &.scroll-extras {
        margin-top: 10px;
      }

      .checkbox-container {
        flex-basis: 100%;
        display: flex;

        .label {
          font-weight: 400;
          white-space: nowrap;
        }

        .phone-preorder-label {
          color: #1f2429;
          font-size: 16px;
          font-weight: 400;
          letter-spacing: -0.3px;
          line-height: 1.5;
          white-space: nowrap;
        }
      }

      .radio-box {
        position: relative;
        cursor: pointer;

        .radio {
          width: 100%;
          height: 100%;
          display: block;
          position: absolute;
          left: 0;
          top: 0;
          z-index: 1;
          background: var(--asf-card-bg, transparent);
          box-shadow: inset 0 0 0 var(--asf-card-border-width, 1px) var(--asf-card-border, #dee2e6);
          border-radius: var(--asf-card-radius, 4px);

          &.on {
            background: var(--asf-card-bg-selected, #e8effd);
            box-shadow: inset 0 0 0 var(--asf-card-border-width-selected, 2px)
              var(--asf-card-border-selected, #145ce6);
          }
        }

        .label {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 13px;
          z-index: 2;
          position: inherit;
          padding: 16px 0;
          font-weight: 700;
          color: var(--asf-card-text, inherit);

          img {
            height: 40px;
          }
        }

        /* 아이콘 위치 (categoryIconPosition) */
        &.pos-bottom .label { flex-direction: column-reverse; }
        /* 좌/우(가로 배치)는 그룹을 가로 가운데 정렬 + 좌우 패딩 (끝에 붙지 않게) */
        &.pos-left .label,
        &.pos-right .label {
          justify-content: center;
          padding-left: 12px;
          padding-right: 12px;
        }
        &.pos-left .label { flex-direction: row; }
        &.pos-right .label { flex-direction: row-reverse; }
      }
    }

    .input__wrap {
      margin-top: 20px;

      .custom-form-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      /* 입력 필드 외곽 (.c-input-text). 검증·마스킹은 불변, 외형만 */
      /* radius 만 커스텀. 색(resting 회색 / 포커스 프라이머리)·두께는 aj-vue-core 기본 유지
         (border-color 를 박으면 포커스 시 프라이머리 보더가 죽으므로 건드리지 않음) */
      .c-input-text {
        border-radius: var(--asf-input-radius, 10px);
      }
    }
  }

  .kakao-request {
    color: #000 !important;
    background: #fee500 !important;
    border-color: #fee500 !important;
    box-shadow: none !important;

    &:disabled {
      opacity: 0.5;
    }
  }

  /* CTA 버튼 스타일 (카카오 버튼은 제외). disabled 상태는 c-button 기본 유지 */
  #submit-btn:not(.kakao-request) {
    border-radius: var(--asf-cta-radius, 12px) !important;

    &:not(:disabled) {
      background: var(--asf-cta-bg, var(--kc-default, #145ce6)) !important;
      border-color: var(--asf-cta-bg, var(--kc-default, #145ce6)) !important;
      color: var(--asf-cta-fg, #fff) !important;
    }
  }

  /* ── Key Color 팔레트 적용 (--kc-* 는 JS가 .secret-form 에 주입, has-kc 일 때만) ── */
  &.has-kc {
    --c-primary600: var(--kc-default);   /* dropdown·CCheckbox 강조 + input 포커스 안쪽 보더용 */
    --c-primary100: var(--kc-disabled);  /* input 포커스 바깥 링 = Disabled (4번째) */
    --asf-card-border-selected: var(--kc-default); /* 선택 카드 보더 = Default (단일) */
    --asf-card-bg-selected: var(--kc-light);       /* 선택 카드 배경 = Light */
    --asf-check-accent: var(--kc-default);         /* 체크박스 면색 = Default */

    /* input 포커스(active) 두겹: 보이는 테두리 = Default(1번째), 바깥 링 = Disabled(4번째, 위 --c-primary100) */
    .c-input-text:focus-within {
      border-color: var(--kc-default) !important;
    }

    /* 약관 동의 체크박스(CCheckbox): 체크 시 파란 사각형(#145CE6 하드코딩 SVG rect)을 key Default 로.
       흰 체크표시(path)는 그대로. 미체크(path 아웃라인)는 rect 가 없어 영향 없음 */
    .agree__box rect[fill='#145CE6'] {
      fill: var(--kc-default) !important;
    }

    /* 버튼 base 는 .has-kc 가 아니라 CTA 규칙의 fallback(var(--asf-cta-bg, var(--kc-default)))으로 처리
       → --asf-cta-bg 로 base 색 개별 override 가능, 미지정 시 Default 따라감 */

    /* 버튼: 보이는 테두리는 border 가 아니라 CButton 의 Tailwind inset-ring(box-shadow) 이라
       border-color 로는 안 바뀜(링 색이 --color-primary-* 에 baked → key color 무시) → box-shadow 를
       직접 kc 팔레트로 덮어써야 링이 key color 를 따라감. 링 색 = 각 상태 배경색과 동일, 텍스트 흰색. */
    #submit-btn:not(.kakao-request) {
      border: none !important;
      /* base 링 = 배경과 동일 (--asf-cta-bg 지정 시 그 색, 미지정 시 kc Default) */
      box-shadow: inset 0 0 0 1px var(--asf-cta-bg, var(--kc-default)) !important;

      &:not(:disabled):hover {
        background: var(--kc-hover) !important;
        box-shadow: inset 0 0 0 1px var(--kc-hover) !important;
      }
      &:not(:disabled):active {
        background: var(--kc-press) !important;
        box-shadow: inset 0 0 0 1px var(--kc-press) !important;
      }
      &:disabled {
        background: var(--kc-disabled) !important;
        box-shadow: inset 0 0 0 1px var(--kc-disabled) !important;
        color: var(--asf-cta-fg, #fff) !important;
        opacity: 1 !important;
      }
    }

    /* 타이틀 강조(.primary-color)는 커스터마이저 대상이 아님 → key-color 미적용, 기본 브랜드색(primary-600) 유지.
       (has-kc 가 --c-primary600 을 kc 로 리맵하므로 타이틀만 브랜드 primary 로 되돌림)
       호스트(랜딩 페이지)가 색을 바꾸고 싶으면 --asf-title-highlight 로 override 가능(그 값이 우선).
       !important 불필요 — .secret-form.has-kc .primary-color(0,3,0) 가 base .primary-color(0,2,0) 를 specificity 로 이김. */
    .primary-color {
      color: var(--asf-title-highlight, var(--color-primary-600, #145ce6));
    }
  }

  /* 타이틀 강조 기본색 = 브랜드 primary. 호스트가 --asf-title-highlight 로 override 가능 */
  .primary-color {
    color: var(--asf-title-highlight, var(--c-primary600, #145ce6));
  }

  @media (max-width: 1024px) {
    width: 100%;

    .white-box {
      width: 100%;
      padding: 36px 16px 48px;
      border-radius: 0;
    }

    &__tit {
      margin-bottom: 20px;

      p {
        font-size: 20px;
        line-height: 28px;
      }
    }
  }
}
</style>
